import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './CampaignDashboard.css';

interface CampaignSummary {
  campaign: string;
  codesGenerated: number;
  codesRedeemed: number;
  totalCodes: number;
  redemptionRate: number;
  mrrAttributed: number;
}

interface ChannelBreakdown {
  channel: string;
  codesGenerated: number;
  codesRedeemed: number;
  redemptionRate: number;
}

interface DiscountTypeBreakdown {
  discountType: string;
  count: number;
  totalRedemptions: number;
}

interface CampaignDetail {
  campaign: string;
  codesGenerated: number;
  codesRedeemed: number;
  totalCodes: number;
  redemptionRate: number;
  mrrAttributed: number;
  channels: ChannelBreakdown[];
  discountTypes: DiscountTypeBreakdown[];
  codes: CampaignCode[];
}

interface CampaignCode {
  id: string;
  code: string;
  status: string;
  discountType: string;
  discountValue: number;
  redemptions: number;
  createdAt: string;
}

const CampaignDashboard: React.FC = () => {
  const { getAccessToken } = useAuth();

  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        return;
      }

      const response = await fetch(API_ENDPOINTS.promoCampaigns(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Failed to load campaigns (${response.status})`);
      }

      const data = await response.json();
      const campaignList: CampaignSummary[] = (data.campaigns || []).sort(
        (a: CampaignSummary, b: CampaignSummary) => (b.totalCodes || 0) - (a.totalCodes || 0)
      );
      setCampaigns(campaignList);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaigns.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignDetail = async (campaignName: string) => {
    setDetailLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        return;
      }

      const response = await fetch(API_ENDPOINTS.promoCampaign(campaignName), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Failed to load campaign details (${response.status})`);
      }

      const data = await response.json();
      setSelectedCampaign(data.campaign || data);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetails = (campaignName: string) => {
    fetchCampaignDetail(campaignName);
  };

  const handleBackToList = () => {
    setSelectedCampaign(null);
    setError(null);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercent = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '--';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '--';
    }
  };

  if (loading) {
    return (
      <div className="campaign-dashboard">
        <div className="campaign-loading">
          <div className="campaign-spinner" />
          <p>Loading campaigns...</p>
        </div>
      </div>
    );
  }

  // Campaign detail view
  if (selectedCampaign) {
    return (
      <div className="campaign-dashboard">
        <div className="campaign-detail-header">
          <button className="campaign-back-btn" onClick={handleBackToList}>
            Back
          </button>
          <h2 className="campaign-detail-name">{selectedCampaign.campaign}</h2>
        </div>

        {error && (
          <div className="campaign-error-banner">
            <span>{error}</span>
            <button
              className="campaign-error-dismiss"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              X
            </button>
          </div>
        )}

        {detailLoading && (
          <div className="campaign-loading">
            <div className="campaign-spinner" />
            <p>Loading campaign details...</p>
          </div>
        )}

        {!detailLoading && (
          <div className="campaign-detail">
            <div className="campaign-detail-stats">
              <div className="campaign-detail-stat">
                <span className="campaign-stat-label">Codes Generated</span>
                <span className="campaign-stat-value">{selectedCampaign.codesGenerated ?? 0}</span>
              </div>
              <div className="campaign-detail-stat">
                <span className="campaign-stat-label">Codes Redeemed</span>
                <span className="campaign-stat-value">{selectedCampaign.codesRedeemed ?? 0}</span>
              </div>
              <div className="campaign-detail-stat">
                <span className="campaign-stat-label">Redemption Rate</span>
                <span className="campaign-stat-value">
                  {selectedCampaign.redemptionRate != null
                    ? formatPercent(selectedCampaign.redemptionRate)
                    : '0%'}
                </span>
              </div>
              <div className="campaign-detail-stat">
                <span className="campaign-stat-label">MRR Attributed</span>
                <span className="campaign-stat-value campaign-mrr">
                  {formatCurrency(selectedCampaign.mrrAttributed ?? 0)}
                </span>
              </div>
            </div>

            {selectedCampaign.channels && selectedCampaign.channels.length > 0 && (
              <div className="campaign-section">
                <h3 className="campaign-section-title">Channel Breakdown</h3>
                <table className="channel-table">
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th>Generated</th>
                      <th>Redeemed</th>
                      <th>Redemption Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCampaign.channels.map((ch, idx) => (
                      <tr key={ch.channel || idx}>
                        <td>{ch.channel || '--'}</td>
                        <td>{ch.codesGenerated ?? 0}</td>
                        <td>{ch.codesRedeemed ?? 0}</td>
                        <td>
                          {ch.redemptionRate != null ? formatPercent(ch.redemptionRate) : '0%'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedCampaign.discountTypes && selectedCampaign.discountTypes.length > 0 && (
              <div className="campaign-section">
                <h3 className="campaign-section-title">Discount Type Breakdown</h3>
                <table className="channel-table">
                  <thead>
                    <tr>
                      <th>Discount Type</th>
                      <th>Count</th>
                      <th>Total Redemptions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCampaign.discountTypes.map((dt, idx) => (
                      <tr key={dt.discountType || idx}>
                        <td>{dt.discountType || '--'}</td>
                        <td>{dt.count ?? 0}</td>
                        <td>{dt.totalRedemptions ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedCampaign.codes && selectedCampaign.codes.length > 0 && (
              <div className="campaign-section">
                <h3 className="campaign-section-title">Codes in Campaign</h3>
                <table className="channel-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Status</th>
                      <th>Discount</th>
                      <th>Redemptions</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCampaign.codes.map((code) => (
                      <tr key={code.id}>
                        <td className="code-cell">{code.code}</td>
                        <td>
                          <span className={`campaign-status-badge status-${code.status}`}>
                            {code.status}
                          </span>
                        </td>
                        <td>
                          {code.discountType === 'percent' || code.discountType === 'percent_off'
                            ? `${code.discountValue}%`
                            : `$${(code.discountValue / 100).toFixed(2)}`}
                        </td>
                        <td>{code.redemptions ?? 0}</td>
                        <td>{formatDate(code.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Campaign list view
  return (
    <div className="campaign-dashboard">
      <h2 className="campaign-dashboard-title">Campaigns</h2>

      {error && (
        <div className="campaign-error-banner">
          <span>{error}</span>
          <button
            className="campaign-error-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            X
          </button>
        </div>
      )}

      {campaigns.length === 0 && !error && (
        <p className="campaign-empty">No campaigns found. Generate promo codes to create campaigns.</p>
      )}

      {campaigns.length > 0 && (
        <div className="campaign-grid">
          {campaigns.map((c, idx) => {
            const redeemed = c.codesRedeemed ?? 0;
            const total = c.totalCodes ?? c.codesGenerated ?? 0;
            const progressPct = total > 0 ? Math.min((redeemed / total) * 100, 100) : 0;

            return (
              <div className="campaign-card" key={c.campaign || idx}>
                <h3 className="campaign-name">{c.campaign || 'Unnamed Campaign'}</h3>
                <div className="campaign-stat">
                  <span className="campaign-stat-lbl">Codes Generated</span>
                  <span className="campaign-stat-val">{c.codesGenerated ?? 0}</span>
                </div>
                <div className="campaign-stat">
                  <span className="campaign-stat-lbl">Redeemed / Total</span>
                  <span className="campaign-stat-val">
                    {redeemed} / {total}
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="campaign-stat">
                  <span className="campaign-stat-lbl">Redemption Rate</span>
                  <span className="campaign-stat-val">
                    {c.redemptionRate != null
                      ? `${(c.redemptionRate * 100).toFixed(1)}%`
                      : '0%'}
                  </span>
                </div>
                <div className="campaign-stat">
                  <span className="campaign-stat-lbl">MRR Attributed</span>
                  <span className="campaign-stat-val campaign-mrr">
                    {formatCurrency(c.mrrAttributed ?? 0)}
                  </span>
                </div>
                <button
                  className="campaign-view-btn"
                  onClick={() => handleViewDetails(c.campaign)}
                >
                  View Details
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampaignDashboard;
